import axios from "axios";
import { GetBaseURL } from "../components/utils/urlUtils";

const api = axios.create({
  baseURL: GetBaseURL(),
});

// Attach auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers = config.headers || {};
    config.headers["x-auth-token"] = token;
  }
  return config;
});

// Public API instance — no auth interceptor (used for public signing pages)
const publicApi = axios.create({
  baseURL: api.defaults.baseURL || "",
});

// Per-request opt out of the global upgrade modal (callers that render their own plan screen)
declare module "axios" {
  interface AxiosRequestConfig {
    skipUpgradeEvent?: boolean;
  }
}

// Response interceptor  -  handle 401 (expired/invalid token) and 403 (upgrade required)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken");
    }

    if (
      error.response?.status === 403 &&
      error.response?.data?.error === "upgrade_required" &&
      !error.config?.skipUpgradeEvent
    ) {
      window.dispatchEvent(new CustomEvent("upgrade-required", {
        detail: error.response.data,
      }));
    }

    return Promise.reject(error);
  }
);

// ─── Documents ──────────────────────────────────────────────────────────────────

export const documentsApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/documents", { params }),

  get: (id: string) =>
    api.get(`/api/documents/${id}`),

  upload: (formData: FormData) =>
    api.post("/api/documents", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/documents/${id}`, data),

  remove: (id: string, reason?: string) =>
    api.delete(`/api/documents/${id}`, { data: { reason } }),

  uploadVersion: (id: string, formData: FormData) =>
    api.post(`/api/documents/${id}/versions`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  listVersions: (id: string) =>
    api.get(`/api/documents/${id}/versions`),

  downloadUrl: (key: string) =>
    `${GetBaseURL()}/api/documents/download/${encodeURIComponent(key)}`,

  downloadBlob: (key: string) =>
    api.get(`/api/documents/download/${encodeURIComponent(key)}`, {
      responseType: "arraybuffer",
    }),

  updateClassification: (id: string, data: Record<string, any>) =>
    api.put(`/api/documents/${id}/classification`, data),

  updateLegalHold: (id: string, legalHold: boolean) =>
    api.put(`/api/documents/${id}/legal-hold`, { legalHold }),

  aiSummarize: (id: string) =>
    api.post(`/api/documents/${id}/ai/summarize`),

  aiGetSummary: (id: string) =>
    api.get(`/api/documents/${id}/ai/summary`),
};

// ─── Document Folders ────────────────────────────────────────────────────────────

export const foldersApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/document-folders", { params }),

  get: (id: string) =>
    api.get(`/api/document-folders/${id}`),

  create: (data: { name: string; parentId?: string; clientId?: string; matterId?: string; color?: string }) =>
    api.post("/api/document-folders", data),

  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/document-folders/${id}`, data),

  remove: (id: string) =>
    api.delete(`/api/document-folders/${id}`),

  getContents: (id: string, params?: Record<string, any>) =>
    api.get(`/api/document-folders/${id}/contents`, { params }),

  getAncestors: (id: string) =>
    api.get(`/api/document-folders/${id}/ancestors`),

  seed: () =>
    api.post("/api/document-folders/seed"),

  updatePermissions: (id: string, data: { accessLevel: "all" | "restricted"; allowedUserIds?: string[] }) =>
    api.put(`/api/document-folders/${id}/permissions`, data),

  reorder: (folderIds: string[]) =>
    api.put("/api/document-folders/reorder", { folderIds }),
};

// ─── Document Templates ─────────────────────────────────────────────────────────

export const templatesApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/document-templates", { params }),

  get: (id: string) =>
    api.get(`/api/document-templates/${id}`),

  create: (data: Record<string, any>) =>
    api.post("/api/document-templates", data),

  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/document-templates/${id}`, data),

  deactivate: (id: string) =>
    api.delete(`/api/document-templates/${id}`),

  generate: (id: string, data: Record<string, any>) =>
    api.post(`/api/document-templates/${id}/generate`, data),
};

// ─── E-Sign ─────────────────────────────────────────────────────────────────────

export const esignApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/esigns", { params }),

  get: (id: string) =>
    api.get(`/api/esigns/${id}`),

  create: (data: Record<string, any>) =>
    api.post("/api/esigns", data),

  send: (id: string) =>
    api.put(`/api/esigns/${id}/send`),

  sign: (id: string, signerIndex: number) =>
    api.put(`/api/esigns/${id}/signers/${signerIndex}/sign`),

  auditTrail: (id: string) =>
    api.get(`/api/esigns/${id}/audit-trail`),

  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/esigns/${id}`, data),

  voidEnvelope: (id: string, reason: string) =>
    api.put(`/api/esigns/${id}/void`, { reason }),

  resend: (id: string, signerIndex?: number) =>
    api.put(`/api/esigns/${id}/resend`, { signerIndex }),

  remind: (id: string, signerIndex?: number) =>
    api.put(`/api/esigns/${id}/remind`, { signerIndex }),

  softDelete: (id: string) =>
    api.delete(`/api/esigns/${id}`),

  bulk: (action: string, ids: string[], reason?: string) =>
    api.post("/api/esigns/bulk", { action, ids, reason }),

  dashboard: () =>
    api.get("/api/esigns/dashboard"),

  download: (id: string) =>
    api.get(`/api/esigns/${id}/download`),
};

// ─── E-Sign Templates ─────────────────────────────────────────────────────────

export const esignTemplatesApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/esign-templates", { params }),
  get: (id: string) =>
    api.get(`/api/esign-templates/${id}`),
  create: (data: Record<string, any>) =>
    api.post("/api/esign-templates", data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/esign-templates/${id}`, data),
  delete: (id: string) =>
    api.delete(`/api/esign-templates/${id}`),
};

// ─── Public E-Sign (no auth) ─────────────────────────────────────────────────

export const publicEsignApi = {
  getSigningData: (token: string) =>
    publicApi.get(`/sign/${token}`),
  submitSignature: (token: string, data: Record<string, any>) =>
    publicApi.post(`/sign/${token}`, data),
  decline: (token: string, reason?: string) =>
    publicApi.post(`/sign/${token}/decline`, { reason }),
};

// ─── Shared Types ───────────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// ─── Clients ────────────────────────────────────────────────────────────────────

export const clientsApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/clients", { params }),
  get: (id: string) =>
    api.get(`/api/clients/${id}`),
  create: (data: Record<string, any>) =>
    api.post("/api/clients", data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/clients/${id}`, data),
  remove: (id: string, reason?: string) =>
    api.delete(`/api/clients/${id}`, { data: { deleteReason: reason } }),
  listWithDocCounts: (params?: Record<string, any>) =>
    api.get("/api/clients/with-doc-counts", { params }),
  getDocuments: (id: string) =>
    api.get(`/api/clients/${id}/documents`),
  getSummary: (id: string) =>
    api.get(`/api/clients/${id}/summary`),
  assign: (id: string, data: Record<string, any>) =>
    api.put(`/api/clients/${id}/assign`, data),
};

// ─── Contacts ───────────────────────────────────────────────────────────────────

export const contactsApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/contacts", { params }),
  get: (id: string) =>
    api.get(`/api/contacts/${id}`),
  create: (data: Record<string, any>) =>
    api.post("/api/contacts", data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/contacts/${id}`, data),
  remove: (id: string, reason?: string) =>
    api.delete(`/api/contacts/${id}`, { data: { deleteReason: reason } }),
};

// ─── Matters ────────────────────────────────────────────────────────────────────

export const mattersApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/matters", { params }),
  get: (id: string) =>
    api.get(`/api/matters/${id}`),
  create: (data: Record<string, any>) =>
    api.post("/api/matters", data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/matters/${id}`, data),
  remove: (id: string, reason?: string) =>
    api.delete(`/api/matters/${id}`, { data: { deleteReason: reason } }),
  changeStatus: (id: string, status: string) =>
    api.put(`/api/matters/${id}/status`, { status }),
  assign: (id: string, data: Record<string, any>) =>
    api.put(`/api/matters/${id}/assign`, data),
  addClientLink: (id: string, data: Record<string, any>) =>
    api.post(`/api/matters/${id}/client-links`, data),
  removeClientLink: (id: string, clientId: string) =>
    api.delete(`/api/matters/${id}/client-links/${clientId}`),
  addImportantDate: (id: string, data: Record<string, any>) =>
    api.post(`/api/matters/${id}/important-dates`, data),
  removeImportantDate: (id: string, dateIndex: number) =>
    api.delete(`/api/matters/${id}/important-dates/${dateIndex}`),
  timeline: (id: string) =>
    api.get(`/api/matters/${id}/timeline`),
  getSummary: (id: string) =>
    api.get(`/api/matters/${id}/summary`),
  changeStage: (id: string, stage: string) =>
    api.put(`/api/matters/${id}/stage`, { stage }),
  getDocumentChecklist: (id: string) =>
    api.get(`/api/matters/${id}/document-checklist`),
  updateDocumentChecklist: (id: string, checklist: any[]) =>
    api.put(`/api/matters/${id}/document-checklist`, { checklist }),
  updateChecklistItem: (id: string, index: number, data: Record<string, any>) =>
    api.put(`/api/matters/${id}/document-checklist/${index}`, data),
  uploadChecklistDocument: (id: string, index: number, formData: FormData) =>
    api.post(`/api/matters/${id}/document-checklist/${index}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  sendDocumentReminder: (id: string, selectedIndices?: number[]) =>
    api.post(`/api/matters/${id}/document-checklist/remind`, selectedIndices ? { selectedIndices } : {}),
  contactClientForDocument: (id: string, index: number, data: { channel: string }) =>
    api.post(`/api/matters/${id}/document-checklist/${index}/contact`, data),
  // External counsel
  addExternalCounsel: (id: string, data: Record<string, any>) =>
    api.post(`/api/matters/${id}/external-counsel`, data),
  updateExternalCounsel: (id: string, counselId: string, data: Record<string, any>) =>
    api.put(`/api/matters/${id}/external-counsel/${counselId}`, data),
  removeExternalCounsel: (id: string, counselId: string) =>
    api.delete(`/api/matters/${id}/external-counsel/${counselId}`),
};

// ─── Undertakings ────────────────────────────────────────────────────────────────

export const undertakingsApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/undertakings", { params }),
  get: (id: string) =>
    api.get(`/api/undertakings/${id}`),
  create: (data: Record<string, any>) =>
    api.post("/api/undertakings", data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/undertakings/${id}`, data),
  remove: (id: string) =>
    api.delete(`/api/undertakings/${id}`),
};

// ─── Tasks ──────────────────────────────────────────────────────────────────────

export const tasksApi = {
  list: (matterId: string, params?: Record<string, any>) =>
    api.get("/api/tasks", { params: { matterId, ...params } }),
  listGrouped: (matterId: string) =>
    api.get("/api/tasks", { params: { matterId, grouped: "true" } }),
  get: (id: string) =>
    api.get(`/api/tasks/${id}`),
  create: (data: Record<string, any>) =>
    api.post("/api/tasks", data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/tasks/${id}`, data),
  remove: (id: string, reason?: string) =>
    api.delete(`/api/tasks/${id}`, { data: { deleteReason: reason } }),
  changeStatus: (id: string, status: string, blockedReason?: string) =>
    api.put(`/api/tasks/${id}/status`, { status, blockedReason }),
  reorder: (id: string, order: number, status?: string) =>
    api.put(`/api/tasks/${id}/reorder`, { order, status }),
  addApproval: (id: string, data: Record<string, any>) =>
    api.post(`/api/tasks/${id}/approvals`, data),
  updateApproval: (id: string, approvalIndex: number, data: Record<string, any>) =>
    api.put(`/api/tasks/${id}/approvals/${approvalIndex}`, data),
};

// ─── Users ──────────────────────────────────────────────────────────────────────

export const usersApi = {
  list: (params?: Record<string, any>) =>
    api.get("/api/users", { params }),
  get: (id: string) =>
    api.get(`/api/users/${id}`),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/users/${id}`, data),
  assignRoles: (id: string, roleIds: string[]) =>
    api.put(`/api/users/${id}/roles`, { roleIds }),
  invite: (data: Record<string, any>) =>
    api.post("/api/users/invite", data),
  deactivate: (id: string) =>
    api.post(`/api/users/${id}/deactivate`),
  reactivate: (id: string) =>
    api.post(`/api/users/${id}/reactivate`),
  batchDeactivate: (userIds: string[]) =>
    api.post("/api/users/batch-deactivate", { userIds }),
};

// ─── Roles ──────────────────────────────────────────────────────────────────────

export const rolesApi = {
  list: () =>
    api.get("/api/roles"),
  create: (data: Record<string, any>) =>
    api.post("/api/roles", data),
  update: (id: string, data: Record<string, any>) =>
    api.put(`/api/roles/${id}`, data),
  remove: (id: string) =>
    api.delete(`/api/roles/${id}`),
};

// ─── Time Entries ────────────────────────────────────────────────────────────
export const timeEntriesApi = {
  list: (params?: Record<string, any>) => api.get("/api/time-entries", { params }),
  get: (id: string) => api.get(`/api/time-entries/${id}`),
  getRunning: () => api.get("/api/time-entries/running"),
  create: (data: Record<string, any>) => api.post("/api/time-entries", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/time-entries/${id}`, data),
  remove: (id: string) => api.delete(`/api/time-entries/${id}`),
  startTimer: (data: Record<string, any>) => api.post("/api/time-entries/timer/start", data),
  stopTimer: (id: string) => api.post(`/api/time-entries/timer/stop/${id}`),
  bulkBill: (data: { timeEntryIds: string[]; invoiceId: string }) => api.put("/api/time-entries/bulk-bill", data),
  writeOff: (id: string) => api.put(`/api/time-entries/${id}/write-off`),
  // AI Suggestions
  getSuggestions: (params?: Record<string, any>) => api.get("/api/time-entries/suggestions", { params }),
  generateSuggestions: (data?: { from?: string; to?: string }) => api.post("/api/time-entries/suggestions/generate", data),
  approveSuggestion: (id: string, data?: Record<string, any>) => api.put(`/api/time-entries/suggestions/${id}/approve`, data),
  bulkApproveSuggestions: (ids: string[]) => api.put("/api/time-entries/suggestions/bulk-approve", { ids }),
  dismissSuggestion: (id: string) => api.delete(`/api/time-entries/suggestions/${id}`),
  // AI Narrative & Reconstruct
  improveNarrative: (data: { narrative: string; matterId?: string; durationMinutes?: number }) =>
    api.post("/api/time-entries/ai/improve-narrative", data),
  reconstructDay: (date: string) =>
    api.post("/api/time-entries/ai/reconstruct", { date }),
  improveBulk: (entryIds: string[]) =>
    api.post("/api/time-entries/ai/improve-bulk", { entryIds }),
  // Review queue
  getReviewEntries: (params?: Record<string, any>) => api.get("/api/time-entries/review", { params }),
  assignMatter: (id: string, data: { matterId: string }) => api.put(`/api/time-entries/${id}/assign-matter`, data),
  splitEntry: (id: string, data: { splits: Array<{ matterId: string; minutes: number; narrative: string }> }) =>
    api.post(`/api/time-entries/${id}/split`, data),
};

// ─── Invoices ────────────────────────────────────────────────────────────────
export const invoicesApi = {
  list: (params?: Record<string, any>) => api.get("/api/invoices", { params }),
  get: (id: string) => api.get(`/api/invoices/${id}`),
  create: (data: Record<string, any>) => api.post("/api/invoices", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/invoices/${id}`, data),
  remove: (id: string) => api.delete(`/api/invoices/${id}`),
  generate: (data: { matterId: string; clientId: string }) => api.post("/api/invoices/generate", data),
  changeStatus: (id: string, status: string) => api.put(`/api/invoices/${id}/status`, { status }),
  approve: (id: string, data: Record<string, any>) => api.put(`/api/invoices/${id}/approve`, data),
  pdf: (id: string) => api.get(`/api/invoices/${id}/pdf`),
  getBuilderData: (id: string) => api.get(`/api/invoices/${id}/builder-data`),
  send: (id: string) => api.post(`/api/invoices/${id}/send`),
  scan: (formData: FormData) =>
    api.post("/api/invoices/scan", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  uploadImage: (id: string, formData: FormData) =>
    api.post(`/api/invoices/${id}/images`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

// ─── AI Invoice Review ──────────────────────────────────────────────────────
export const aiInvoiceApi = {
  reviewEntries: (data: { entryIds?: string[]; matterId?: string; dateRange?: { from: string; to: string } }) =>
    api.post("/api/invoices/ai/review-entries", data),
};

// ─── Payments ────────────────────────────────────────────────────────────────
export const paymentsApi = {
  list: (params?: Record<string, any>) => api.get("/api/payments", { params }),
  get: (id: string) => api.get(`/api/payments/${id}`),
  create: (data: Record<string, any>) => api.post("/api/payments", data),
  refund: (id: string) => api.put(`/api/payments/${id}/refund`),
  addAttachment: (id: string, formData: FormData) =>
    api.post(`/api/payments/${id}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  removeAttachment: (id: string, docId: string) =>
    api.delete(`/api/payments/${id}/attachments/${docId}`),
};

// ─── Rate Tables ─────────────────────────────────────────────────────────────
export const rateTablesApi = {
  list: () => api.get("/api/rate-tables"),
  get: (id: string) => api.get(`/api/rate-tables/${id}`),
  create: (data: Record<string, any>) => api.post("/api/rate-tables", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/rate-tables/${id}`, data),
  remove: (id: string) => api.delete(`/api/rate-tables/${id}`),
  setDefault: (id: string) => api.put(`/api/rate-tables/${id}/set-default`),
};

// ─── Matter Rates ────────────────────────────────────────────────────────────
export const matterRatesApi = {
  list: (matterId: string) => api.get("/api/matter-rates", { params: { matterId } }),
  create: (data: Record<string, any>) => api.post("/api/matter-rates", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/matter-rates/${id}`, data),
  remove: (id: string) => api.delete(`/api/matter-rates/${id}`),
};

// ─── Fee Items ───────────────────────────────────────────────────────────────
export const feeItemsApi = {
  list: (params?: Record<string, any>) => api.get("/api/fee-items", { params }),
  get: (id: string) => api.get(`/api/fee-items/${id}`),
  create: (data: Record<string, any>) => api.post("/api/fee-items", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/fee-items/${id}`, data),
  remove: (id: string) => api.delete(`/api/fee-items/${id}`),
  categories: () => api.get("/api/fee-items/categories"),
};

// ─── Discounts ───────────────────────────────────────────────────────────────
export const discountsApi = {
  list: (params?: Record<string, any>) => api.get("/api/discounts", { params }),
  create: (data: Record<string, any>) => api.post("/api/discounts", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/discounts/${id}`, data),
  remove: (id: string) => api.delete(`/api/discounts/${id}`),
};

// ─── Payment Plans ───────────────────────────────────────────────────────────
export const paymentPlansApi = {
  list: (params?: Record<string, any>) => api.get("/api/payment-plans", { params }),
  get: (id: string) => api.get(`/api/payment-plans/${id}`),
  create: (data: Record<string, any>) => api.post("/api/payment-plans", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/payment-plans/${id}`, data),
  cancel: (id: string, reason?: string) => api.put(`/api/payment-plans/${id}/cancel`, { reason }),
  payInstallment: (id: string, index: number, paymentId: string) => api.put(`/api/payment-plans/${id}/installments/${index}/pay`, { paymentId }),
};

// ─── Twilio Numbers ──────────────────────────────────────────────────────────
export const twilioApi = {
  listNumbers: (params?: Record<string, any>) => api.get("/api/twilio/numbers", { params }),
  getNumber: (id: string) => api.get(`/api/twilio/numbers/${id}`),
  searchAvailable: (params: { country: string; city?: string }) =>
    api.get("/api/twilio/numbers/available", { params }),
  suggestCities: (params: { q: string; country?: string }) =>
    api.get("/api/twilio/cities/suggest", { params }),
  purchase: (data: Record<string, any>) => api.post("/api/twilio/numbers/purchase", data),
  updateNumber: (id: string, data: Record<string, any>) => api.put(`/api/twilio/numbers/${id}`, data),
  releaseNumber: (id: string) => api.put(`/api/twilio/numbers/${id}/release`),
  portNumber: (data: FormData) => api.post("/api/twilio/numbers/port", data, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  assignNumber: (id: string, userId: string) => api.put(`/api/twilio/numbers/${id}/assign`, { userId }),
  syncWebhooks: (id: string) => api.post(`/api/twilio/numbers/${id}/sync-webhooks`),
  syncAllWebhooks: () => api.post("/api/twilio/numbers/sync-all-webhooks"),
};

// ─── Conversations ───────────────────────────────────────────────────────────
export const conversationsApi = {
  list: (params?: Record<string, any>) => api.get("/api/conversations", { params }),
  get: (id: string) => api.get(`/api/conversations/${id}`),
  create: (data: Record<string, any>) => api.post("/api/conversations", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/conversations/${id}`, data),
  archive: (id: string) => api.put(`/api/conversations/${id}/archive`),
  markRead: (id: string) => api.put(`/api/conversations/${id}/read`),
  remove: (id: string) => api.delete(`/api/conversations/${id}`),
};

// ─── Messages ────────────────────────────────────────────────────────────────
export const messagesApi = {
  list: (conversationId: string, params?: Record<string, any>) =>
    api.get("/api/messages", { params: { conversationId, ...params } }),
  get: (id: string) => api.get(`/api/messages/${id}`),
  send: (data: {
    conversationId: string;
    channel: string;
    to: string | string[];
    body: string;
    from?: string;
    bodyHtml?: string;
    subject?: string;
    emailConnectionId?: string;
    inReplyToMessageId?: string;
    cc?: string[];
    bcc?: string[];
    attachments?: Array<{ key: string; filename: string; mimeType: string; sizeBytes: number }>;
  }) => api.post("/api/messages", data),
  markRead: (id: string) => api.put(`/api/messages/${id}/read`),
  uploadAttachment: (formData: FormData) =>
    api.post("/api/messages/attachments", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  getAttachmentUrl: (messageId: string, index: number) =>
    api.get(`/api/messages/${messageId}/attachments/${index}/download`),
};

// ─── Email Connections ───────────────────────────────────────────────────────
export const emailConnectionsApi = {
  list: () => api.get("/api/email-connections"),
  get: (id: string) => api.get(`/api/email-connections/${id}`),
  create: (data: Record<string, any>) => api.post("/api/email-connections", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/email-connections/${id}`, data),
  pause: (id: string) => api.put(`/api/email-connections/${id}/pause`),
  resume: (id: string) => api.put(`/api/email-connections/${id}/resume`),
  disconnect: (id: string) => api.delete(`/api/email-connections/${id}`),
  getAuthUrl: (provider: string) => api.get(`/api/email-connections/auth/${provider}`),
  callback: (provider: string, code: string, state: string) =>
    api.get(`/api/email-connections/callback/${provider}`, { params: { code, state } }),
  sync: (id: string, data: { contactEmail: string; conversationId: string; after?: string; before?: string; clientId?: string; matterId?: string }) =>
    api.post(`/api/email-connections/${id}/sync`, data),
  syncStatus: (id: string, contactEmail: string) =>
    api.get(`/api/email-connections/${id}/sync-status`, { params: { contactEmail } }),
};

// ─── Voice ──────────────────────────────────────────────────────────────────
export const voiceApi = {
  getToken: () => api.get("/api/voice/token"),
  initiateCall: (data: { to: string; from: string; conversationId: string }) =>
    api.post("/api/voice/call", data),
  getCall: (id: string) => api.get(`/api/voice/call/${id}`),
  updateCallNotes: (id: string, notes: string) =>
    api.put(`/api/voice/call/${id}/notes`, { notes }),
  endCall: (id: string, data?: { status?: string; durationSecs?: number }) =>
    api.put(`/api/voice/call/${id}/end`, data || {}),
  addCallNote: (id: string, data: { body: string; mentions?: string[] }) =>
    api.post(`/api/voice/call/${id}/notes`, data),
  getCallNotes: (id: string) =>
    api.get(`/api/voice/call/${id}/notes`),
};

// ─── Internal Comments ──────────────────────────────────────────────────────
export const internalCommentsApi = {
  list: (params: { conversationId?: string; entityType?: string; entityId?: string; limit?: number }) =>
    api.get("/api/internal-comments", { params }),
  create: (data: { conversationId: string; entityType: string; entityId: string; body: string; mentions?: string[]; parentCommentId?: string }) =>
    api.post("/api/internal-comments", data),
  update: (id: string, data: { body: string; mentions?: string[] }) =>
    api.put(`/api/internal-comments/${id}`, data),
  toggleReaction: (id: string, emoji: string) =>
    api.post(`/api/internal-comments/${id}/reactions`, { emoji }),
  remove: (id: string) => api.delete(`/api/internal-comments/${id}`),
};

// ─── Call Logs ──────────────────────────────────────────────────────────────
export const callLogsApi = {
  list: (params?: Record<string, any>) => api.get("/api/call-logs", { params }),
  get: (id: string) => api.get(`/api/call-logs/${id}`),
  markResponded: (id: string, via: string) =>
    api.put(`/api/call-logs/${id}/respond`, { respondedVia: via }),
};

// ─── Timeline ───────────────────────────────────────────────────────────────
export const timelineApi = {
  list: (conversationId: string, params?: Record<string, any>) =>
    api.get("/api/timeline", { params: { conversationId, ...params } }),
};

// ─── Trust ───────────────────────────────────────────────────────────────────
export const trustApi = {
  listReceipts: (params?: Record<string, any>) => api.get("/api/trust/receipts", { params }),
  getReceipt: (id: string) => api.get(`/api/trust/receipts/${id}`),
  createReceipt: (data: Record<string, any>) => api.post("/api/trust/receipts", data),
  clearReceipt: (id: string) => api.put(`/api/trust/receipts/${id}/clear`),
  voidReceipt: (id: string, reason: string) => api.put(`/api/trust/receipts/${id}/void`, { reason }),
  listDisbursements: (params?: Record<string, any>) => api.get("/api/trust/disbursements", { params }),
  getDisbursement: (id: string) => api.get(`/api/trust/disbursements/${id}`),
  createDisbursement: (data: Record<string, any>) => api.post("/api/trust/disbursements", data),
  submitDisbursement: (id: string) => api.put(`/api/trust/disbursements/${id}/submit`),
  approveDisbursement: (id: string, note?: string) => api.put(`/api/trust/disbursements/${id}/approve`, { note }),
  rejectDisbursement: (id: string, note: string) => api.put(`/api/trust/disbursements/${id}/reject`, { note }),
  releaseDisbursement: (id: string, data?: Record<string, any>) => api.put(`/api/trust/disbursements/${id}/release`, data),
  voidDisbursement: (id: string, reason: string) => api.put(`/api/trust/disbursements/${id}/void`, { reason }),
  getSubledger: (params?: Record<string, any>) => api.get("/api/trust/subledger", { params }),
  getSubledgerSummary: (params?: Record<string, any>) => api.get("/api/trust/subledger/summary", { params }),
};

// ─── Bank Accounts ───────────────────────────────────────────────────────────
export const bankAccountsApi = {
  list: (params?: Record<string, any>) => api.get("/api/bank-accounts", { params }),
  get: (id: string) => api.get(`/api/bank-accounts/${id}`),
  create: (data: Record<string, any>) => api.post("/api/bank-accounts", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/bank-accounts/${id}`, data),
  close: (id: string) => api.put(`/api/bank-accounts/${id}/close`),
};

// ─── Journal Entries ─────────────────────────────────────────────────────────
export const journalEntriesApi = {
  list: (params?: Record<string, any>) => api.get("/api/journal-entries", { params }),
  get: (id: string) => api.get(`/api/journal-entries/${id}`),
  create: (data: Record<string, any>) => api.post("/api/journal-entries", data),
  reverse: (id: string) => api.post(`/api/journal-entries/${id}/reverse`),
};

// ─── Chart of Accounts ───────────────────────────────────────────────────────
export const chartOfAccountsApi = {
  list: (params?: Record<string, any>) => api.get("/api/chart-of-accounts", { params }),
  balances: (params?: Record<string, any>) => api.get("/api/chart-of-accounts/balances", { params }),
  create: (data: Record<string, any>) => api.post("/api/chart-of-accounts", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/chart-of-accounts/${id}`, data),
  deactivate: (id: string) => api.put(`/api/chart-of-accounts/${id}/deactivate`),
  reactivate: (id: string) => api.put(`/api/chart-of-accounts/${id}/reactivate`),
  seed: (data?: Record<string, any>) => api.post("/api/chart-of-accounts/seed", data || {}),
};

// ─── Reconciliations ─────────────────────────────────────────────────────────
export const reconciliationsApi = {
  list: (params?: Record<string, any>) => api.get("/api/reconciliations", { params }),
  get: (id: string) => api.get(`/api/reconciliations/${id}`),
  create: (data: Record<string, any>) => api.post("/api/reconciliations", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/reconciliations/${id}`, data),
  finalize: (id: string) => api.put(`/api/reconciliations/${id}/finalize`),
  uploadStatement: (data: Record<string, any>) => api.post("/api/reconciliations/statements", data),
  listStatements: (params?: Record<string, any>) => api.get("/api/reconciliations/statements", { params }),
};

// ─── Payout Requests ─────────────────────────────────────────────────────────
export const payoutRequestsApi = {
  list: (params?: Record<string, any>) => api.get("/api/payout-requests", { params }),
  get: (id: string) => api.get(`/api/payout-requests/${id}`),
  create: (data: Record<string, any>) => api.post("/api/payout-requests", data),
  submit: (id: string) => api.put(`/api/payout-requests/${id}/submit`),
  approve: (id: string, note?: string) => api.put(`/api/payout-requests/${id}/approve`, { note }),
  reject: (id: string, note: string) => api.put(`/api/payout-requests/${id}/reject`, { note }),
  release: (id: string, data?: Record<string, any>) => api.put(`/api/payout-requests/${id}/release`, data),
  cancel: (id: string) => api.put(`/api/payout-requests/${id}/cancel`),
};

// ─── Accounting Profiles ─────────────────────────────────────────────────────
export const accountingProfilesApi = {
  list: () => api.get("/api/accounting-profiles"),
  get: (userId: string) => api.get(`/api/accounting-profiles/${userId}`),
  upsert: (data: Record<string, any>) => api.post("/api/accounting-profiles", data),
  update: (userId: string, data: Record<string, any>) => api.put(`/api/accounting-profiles/${userId}`, data),
};

// ─── Bank Transactions ──────────────────────────────────────────────────────
export const bankTransactionsApi = {
  preview: (formData: FormData) =>
    api.post("/api/bank-transactions/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  import: (formData: FormData) =>
    api.post("/api/bank-transactions/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  create: (data: Record<string, any>) => api.post("/api/bank-transactions", data),
  list: (params?: Record<string, any>) => api.get("/api/bank-transactions", { params }),
  update: (id: string, data: Record<string, any>) => api.put(`/api/bank-transactions/${id}`, data),
  deleteBatch: (batchId: string) => api.delete(`/api/bank-transactions/batch/${batchId}`),
};

// ─── Accounting (unified) ────────────────────────────────────────────────────
export const accountingApi = {
  clientBalances: (params?: Record<string, any>) =>
    api.get("/api/accounting/client-balances", { params }),
};

// ─── Financial Statements ───────────────────────────────────────────────────
export const financialStatementsApi = {
  trialBalance: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/trial-balance", { params }),
  balanceSheet: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/balance-sheet", { params }),
  incomeStatement: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/income-statement", { params }),
  exportTrialBalance: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/trial-balance/export", { params, responseType: "blob" }),
  exportBalanceSheet: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/balance-sheet/export", { params, responseType: "blob" }),
  exportIncomeStatement: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/income-statement/export", { params, responseType: "blob" }),
  exportTrialBalancePDF: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/trial-balance/export", { params: { ...params, format: "pdf" }, responseType: "blob" }),
  exportBalanceSheetPDF: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/balance-sheet/export", { params: { ...params, format: "pdf" }, responseType: "blob" }),
  exportIncomeStatementPDF: (params?: Record<string, any>) =>
    api.get("/api/financial-statements/income-statement/export", { params: { ...params, format: "pdf" }, responseType: "blob" }),
};

// ─── Pipeline Stages ─────────────────────────────────────────────────────────
export const pipelineStagesApi = {
  list: (pipelineType: string) => api.get("/api/pipeline-stages", { params: { pipelineType } }),
  create: (data: Record<string, any>) => api.post("/api/pipeline-stages", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/pipeline-stages/${id}`, data),
  reorder: (stages: { _id: string; order: number }[]) => api.put("/api/pipeline-stages/reorder", { stages }),
  remove: (id: string) => api.delete(`/api/pipeline-stages/${id}`),
};

// ─── Immigration ─────────────────────────────────────────────────────────────
export const immigrationApi = {
  listCases: (params?: Record<string, any>) => api.get("/api/immigration/cases", { params }),
  getCase: (id: string) => api.get(`/api/immigration/cases/${id}`),
  createCase: (data: Record<string, any>) => api.post("/api/immigration/cases", data),
  updateCase: (id: string, data: Record<string, any>) => api.put(`/api/immigration/cases/${id}`, data),
  deleteCase: (id: string) => api.delete(`/api/immigration/cases/${id}`),
  addParty: (id: string, data: Record<string, any>) => api.post(`/api/immigration/cases/${id}/parties`, data),
  updateParty: (id: string, index: number, data: Record<string, any>) => api.put(`/api/immigration/cases/${id}/parties/${index}`, data),
  removeParty: (id: string, index: number) => api.delete(`/api/immigration/cases/${id}/parties/${index}`),
  updateMilestone: (id: string, index: number, data: Record<string, any>) => api.put(`/api/immigration/cases/${id}/milestones/${index}`, data),
  addMilestone: (id: string, data: Record<string, any>) => api.post(`/api/immigration/cases/${id}/milestones`, data),
  removeMilestone: (id: string, index: number) => api.delete(`/api/immigration/cases/${id}/milestones/${index}`),
  reorderMilestones: (id: string, order: number[]) => api.put(`/api/immigration/cases/${id}/milestones/reorder`, { order }),
  addFiling: (id: string, data: Record<string, any>) => api.post(`/api/immigration/cases/${id}/filings`, data),
  updateFiling: (id: string, index: number, data: Record<string, any>) => api.put(`/api/immigration/cases/${id}/filings/${index}`, data),
  removeFiling: (id: string, index: number) => api.delete(`/api/immigration/cases/${id}/filings/${index}`),
  getProgramData: (id: string) => api.get(`/api/immigration/cases/${id}/program-data`),
  updateProgramData: (id: string, data: Record<string, any>) => api.put(`/api/immigration/cases/${id}/program-data`, data),
  changeCaseStage: (id: string, stage: string) => api.put(`/api/immigration/cases/${id}/stage`, { stage }),
  getPreferences: (programKey: string) => api.get(`/api/immigration/preferences/${programKey}`),
  savePreferences: (programKey: string, data: Record<string, any>) => api.put(`/api/immigration/preferences/${programKey}`, data),
};

export const immigrationProgramsApi = {
  list: (params?: Record<string, any>) => api.get("/api/immigration-programs", { params }),
  get: (id: string) => api.get(`/api/immigration-programs/${id}`),
  create: (data: Record<string, any>) => api.post("/api/immigration-programs", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/immigration-programs/${id}`, data),
  deactivate: (id: string) => api.put(`/api/immigration-programs/${id}/deactivate`),
  seed: () => api.post("/api/immigration-programs/seed"),
};

// ─── Real Estate ─────────────────────────────────────────────────────────────
export const realEstateApi = {
  listProperties: (params?: Record<string, any>) => api.get("/api/real-estate/properties", { params }),
  getProperty: (id: string) => api.get(`/api/real-estate/properties/${id}`),
  createProperty: (data: Record<string, any>) => api.post("/api/real-estate/properties", data),
  updateProperty: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/properties/${id}`, data),
  deleteProperty: (id: string) => api.delete(`/api/real-estate/properties/${id}`),
  listTransactions: (params?: Record<string, any>) => api.get("/api/real-estate/transactions", { params }),
  getTransaction: (id: string) => api.get(`/api/real-estate/transactions/${id}`),
  createTransaction: (data: Record<string, any>) => api.post("/api/real-estate/transactions", data),
  updateTransaction: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/transactions/${id}`, data),
  changeStatus: (id: string, status: string) => api.put(`/api/real-estate/transactions/${id}/status`, { status }),
  addDeposit: (id: string, data: Record<string, any>) => api.post(`/api/real-estate/transactions/${id}/deposits`, data),
  updateDeposit: (id: string, index: number, data: Record<string, any>) => api.put(`/api/real-estate/transactions/${id}/deposits/${index}`, data),
  removeDeposit: (id: string, index: number) => api.delete(`/api/real-estate/transactions/${id}/deposits/${index}`),
  getSoa: (id: string) => api.get(`/api/real-estate/transactions/${id}/soa`),
  updateSoa: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/transactions/${id}/soa`, data),
  updateClosingFunds: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/transactions/${id}/closing-funds`, data),
  approveClosingFunds: (id: string, note?: string) => api.post(`/api/real-estate/transactions/${id}/closing-funds/approve`, { note }),
  rejectClosingFunds: (id: string, note: string) => api.post(`/api/real-estate/transactions/${id}/closing-funds/reject`, { note }),
  generateAuditPackage: (id: string) => api.post(`/api/real-estate/transactions/${id}/audit-package`),
};

// ─── Real Estate Cases (new case model) ─────────────────────────────────────
export const realEstateCasesApi = {
  list: (params?: Record<string, any>) => api.get("/api/real-estate/cases", { params }),
  get: (id: string) => api.get(`/api/real-estate/cases/${id}`),
  create: (data: Record<string, any>) => api.post("/api/real-estate/cases", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}`, data),
  remove: (id: string) => api.delete(`/api/real-estate/cases/${id}`),
  updatePipelineStage: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/pipeline-stage`, data),
  updateParties: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/parties`, data),
  updateOtherSide: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/parties/other-side`, data),
  getSoa: (id: string) => api.get(`/api/real-estate/cases/${id}/soa`),
  updateSoa: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/soa`, data),
  generateSoa: (id: string) => api.post(`/api/real-estate/cases/${id}/soa/generate`, {}, { responseType: "blob" }),
  updateClosingFunds: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/closing-funds`, data),
  approveClosingFunds: (id: string, data?: Record<string, any>) => api.post(`/api/real-estate/cases/${id}/closing-funds/approve`, data),
  rejectClosingFunds: (id: string, data?: Record<string, any>) => api.post(`/api/real-estate/cases/${id}/closing-funds/reject`, data),
  updateTitle: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/title`, data),
  addInstrument: (id: string, data: Record<string, any>) => api.post(`/api/real-estate/cases/${id}/title/instruments`, data),
  addWritSearch: (id: string, data: Record<string, any>) => api.post(`/api/real-estate/cases/${id}/title/writ-searches`, data),
  updateLtt: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/ltt`, data),
  updateToronto: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/toronto`, data),
  updateCompliance: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/cases/${id}/compliance`, data),
  closeReadyCheck: (id: string) => api.post(`/api/real-estate/cases/${id}/close-ready-check`),
};

// ─── Real Estate Programs (templates) ───────────────────────────────────────
export const realEstateProgramsApi = {
  list: (params?: Record<string, any>) => api.get("/api/real-estate/programs", { params }),
  get: (id: string) => api.get(`/api/real-estate/programs/${id}`),
  seed: () => api.post("/api/real-estate/programs/seed"),
  create: (data: Record<string, any>) => api.post("/api/real-estate/programs", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/real-estate/programs/${id}`, data),
};

// ─── Real Estate Documents (PDF generation) ─────────────────────────────────
export const realEstateDocumentsApi = {
  templates: () => api.get("/api/real-estate/documents/templates"),
  generateClosingReport: (caseId: string) => api.post(`/api/real-estate/documents/generate/closing-report/${caseId}`, {}, { responseType: "blob" }),
  generateSoa: (caseId: string) => api.post(`/api/real-estate/documents/generate/soa/${caseId}`, {}, { responseType: "blob" }),
  generateAccountStatement: (caseId: string) => api.post(`/api/real-estate/documents/generate/account-statement/${caseId}`, {}, { responseType: "blob" }),
  assemblePackage: (caseId: string, data?: Record<string, any>) => api.post(`/api/real-estate/documents/assemble-package/${caseId}`, data || {}, { responseType: "blob" }),
};

// ─── Task Pipeline Extensions ───────────────────────────────────────────────
export const taskPipelineApi = {
  getByMatter: (matterId: string) => api.get(`/api/tasks/pipeline/${matterId}`),
  updateStage: (taskId: string, data: Record<string, any>) => api.put(`/api/tasks/${taskId}/pipeline-stage`, data),
  attachEvidence: (taskId: string, data: Record<string, any>) => api.put(`/api/tasks/${taskId}/evidence`, data),
  getDependencies: (taskId: string) => api.get(`/api/tasks/${taskId}/dependencies`),
};

// ─── Calendar ────────────────────────────────────────────────────────────────
export const calendarApi = {
  list: (params?: Record<string, any>) => api.get("/api/calendar", { params }),
  get: (id: string) => api.get(`/api/calendar/${id}`),
  create: (data: Record<string, any>) => api.post("/api/calendar", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/calendar/${id}`, data),
  remove: (id: string) => api.delete(`/api/calendar/${id}`),
  upcoming: () => api.get("/api/calendar/upcoming"),
};

// ─── Calendar Integrations ───────────────────────────────────────────────────
export const calendarIntegrationsApi = {
  list: () => api.get("/api/calendar-integrations"),
  getAuthUrl: (provider: string) =>
    api.get(`/api/calendar-integrations/auth/${provider}`),
  callback: (provider: string, code: string, state: string) =>
    api.get(`/api/calendar-integrations/callback/${provider}`, { params: { code, state } }),
  disconnect: (id: string) =>
    api.delete(`/api/calendar-integrations/${id}`),
  sync: (id: string) =>
    api.post(`/api/calendar-integrations/sync/${id}`),
};

// ─── Booking Types (authenticated) ──────────────────────────────────────────
export const bookingTypesApi = {
  list: (params?: Record<string, any>) => api.get("/api/booking-types", { params }),
  get: (id: string) => api.get(`/api/booking-types/${id}`),
  create: (data: Record<string, any>) => api.post("/api/booking-types", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/booking-types/${id}`, data),
  remove: (id: string) => api.delete(`/api/booking-types/${id}`),
  checkSlug: (slug: string, excludeId?: string) =>
    api.get(`/api/booking-types/check-slug/${slug}`, { params: excludeId ? { excludeId } : {} }),
};

// ─── Bookings (authenticated) ───────────────────────────────────────────────
export const bookingsApi = {
  list: (params?: Record<string, any>) => api.get("/api/bookings", { params }),
  get: (id: string) => api.get(`/api/bookings/${id}`),
  updateStatus: (id: string, data: Record<string, any>) => api.put(`/api/bookings/${id}/status`, data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/bookings/${id}`, data),
  cancel: (id: string, data?: Record<string, any>) => api.post(`/api/bookings/${id}/cancel`, data),
  reschedule: (id: string, data: Record<string, any>) => api.post(`/api/bookings/${id}/reschedule`, data),
  remove: (id: string) => api.delete(`/api/bookings/${id}`),
};

// ─── Public Booking (no auth) ───────────────────────────────────────────────
export const publicBookingApi = {
  getBookingType: (firmSlug: string, bookingSlug: string) =>
    publicApi.get(`/book/${firmSlug}/${bookingSlug}`),
  getSlots: (firmSlug: string, bookingSlug: string, params: { startDate: string; endDate: string }) =>
    publicApi.get(`/book/${firmSlug}/${bookingSlug}/slots`, { params }),
  submitBooking: (firmSlug: string, bookingSlug: string, data: Record<string, any>) =>
    publicApi.post(`/book/${firmSlug}/${bookingSlug}`, data),
  getManageData: (manageToken: string) =>
    publicApi.get(`/book/manage/${manageToken}`),
  cancelBooking: (manageToken: string, data?: Record<string, any>) =>
    publicApi.post(`/book/manage/${manageToken}/cancel`, data),
  rescheduleBooking: (manageToken: string, data: Record<string, any>) =>
    publicApi.post(`/book/manage/${manageToken}/reschedule`, data),
  getSlotsByToken: (manageToken: string, params: { startDate: string; endDate: string }) =>
    publicApi.get(`/book/manage/${manageToken}/slots`, { params }),
};

// ─── Notifications ───────────────────────────────────────────────────────────
export const notificationsApi = {
  list: (params?: Record<string, any>) => api.get("/api/notifications", { params }),
  unreadCount: () => api.get("/api/notifications/unread-count"),
  markRead: (id: string) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.put("/api/notifications/mark-all-read"),
};

// ─── Intake ──────────────────────────────────────────────────────────────────
export const intakeApi = {
  listForms: () => api.get("/api/intake/forms"),
  getForm: (id: string) => api.get(`/api/intake/forms/${id}`),
  createForm: (data: Record<string, any>) => api.post("/api/intake/forms", data),
  updateForm: (id: string, data: Record<string, any>) => api.put(`/api/intake/forms/${id}`, data),
  deactivateForm: (id: string) => api.put(`/api/intake/forms/${id}/deactivate`),
  listStages: () => api.get("/api/intake/stages"),
  listSubmissions: (params?: Record<string, any>) => api.get("/api/intake/submissions", { params }),
  getSubmission: (id: string) => api.get(`/api/intake/submissions/${id}`),
  createSubmission: (data: Record<string, any>) => api.post("/api/intake/submissions", data),
  updateSubmission: (id: string, data: Record<string, any>) => api.put(`/api/intake/submissions/${id}`, data),
  convertSubmission: (id: string, data: Record<string, any>) => api.post(`/api/intake/submissions/${id}/convert`, data),
};

// ─── Audit Logs ──────────────────────────────────────────────────────────────
export const auditLogsApi = {
  list: (params?: Record<string, any>) => api.get("/api/audit-logs", { params }),
  get: (id: string) => api.get(`/api/audit-logs/${id}`),
};

// ─── Client Portal ───────────────────────────────────────────────────────────
export const portalApi = {
  myMatters: (params?: Record<string, any>) => api.get("/api/portal/my-matters", { params }),
  myInvoices: (params?: Record<string, any>) => api.get("/api/portal/my-invoices", { params }),
  myDocuments: (params?: Record<string, any>) => api.get("/api/portal/my-documents", { params }),
  submitPayment: (data: Record<string, any>) => api.post("/api/portal/my-payments", data),
  sendMessage: (data: Record<string, any>) => api.post("/api/portal/my-messages", data),
  myPaymentPlans: (params?: Record<string, any>) => api.get("/api/portal/my-payment-plans", { params }),
};

// ─── Stripe Connect ─────────────────────────────────────────────────────────
export const stripeConnectApi = {
  createAccountLink: (data?: { returnUrl?: string }) => api.post("/api/stripe-connect/create-account-link", data),
  getStatus: () => api.get("/api/stripe-connect/status"),
  getDashboardLink: () => api.post("/api/stripe-connect/dashboard-link"),
};

// ─── Payment Settings ───────────────────────────────────────────────────────
export const paymentSettingsApi = {
  get: () => api.get("/api/payment-settings"),
  update: (data: Record<string, any>) => api.put("/api/payment-settings", data),
};

// ─── Reports ─────────────────────────────────────────────────────────────────
export const reportsApi = {
  revenue: (params?: Record<string, any>) => api.get("/api/reports/revenue", { params }),
  outstandingInvoices: (params?: Record<string, any>) => api.get("/api/reports/outstanding-invoices", { params }),
  trustBalances: (params?: Record<string, any>) => api.get("/api/reports/trust-balances", { params }),
  timeSummary: (params?: Record<string, any>) => api.get("/api/reports/time-summary", { params }),
  matterOverview: (params?: Record<string, any>) => api.get("/api/reports/matter-overview", { params }),
};

// ─── Matter Notes ───────────────────────────────────────────────────────────
export const matterNotesApi = {
  list: (matterId: string, params?: Record<string, any>) =>
    api.get("/api/matter-notes", { params: { matterId, ...params } }),
  create: (data: { matterId: string; body: string; isPinned?: boolean }) =>
    api.post("/api/matter-notes", data),
  update: (id: string, data: { body: string }) =>
    api.put(`/api/matter-notes/${id}`, data),
  remove: (id: string) =>
    api.delete(`/api/matter-notes/${id}`),
  togglePin: (id: string) =>
    api.put(`/api/matter-notes/${id}/pin`),
};

// ─── Dashboard ─────────────────────────────────────────────────────────────
export const dashboardApi = {
  get: () => api.get("/api/dashboard"),
};

// ─── Account Summaries ──────────────────────────────────────────────────────
export const accountSummariesApi = {
  list: (params?: Record<string, any>) => api.get("/api/account-summaries", { params }),
  get: (id: string) => api.get(`/api/account-summaries/${id}`),
  create: (data: Record<string, any>) => api.post("/api/account-summaries", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/account-summaries/${id}`, data),
  changeStatus: (id: string, status: string) => api.put(`/api/account-summaries/${id}/status`, { status }),
  getBuilderData: (id: string) => api.get(`/api/account-summaries/${id}/builder-data`),
  regenerate: (id: string) => api.post(`/api/account-summaries/${id}/regenerate`),
};

// ─── Billing Templates ──────────────────────────────────────────────────────
export const billingTemplatesApi = {
  list: (params?: Record<string, any>) => api.get("/api/billing-templates", { params }),
  get: (id: string) => api.get(`/api/billing-templates/${id}`),
  create: (data: Record<string, any>) => api.post("/api/billing-templates", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/billing-templates/${id}`, data),
  remove: (id: string) => api.delete(`/api/billing-templates/${id}`),
};

// ─── Data Import ────────────────────────────────────────────────────────────
export const dataImportApi = {
  previewClients: (formData: FormData) =>
    api.post("/api/import/clients/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  importClients: (formData: FormData) =>
    api.post("/api/import/clients/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  previewContacts: (formData: FormData) =>
    api.post("/api/import/contacts/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  importContacts: (formData: FormData) =>
    api.post("/api/import/contacts/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  undoBatch: (entityType: string, batchId: string) =>
    api.delete(`/api/import/${entityType}/batch/${batchId}`),
};

// ─── Billing (subscription) ─────────────────────────────────────────────────
export const billingApi = {
  charge: (data: { plan: number; duration: string; paymentMethodId: string; seatCount?: number }) =>
    api.post("/api/billing/charge", data),
  getCurrentCard: () => api.get("/api/billing/current-card"),
  updateCard: (data: { paymentMethodId: string }) => api.post("/api/billing/update-card", data),
  cancelSubscription: () => api.post("/api/billing/confirm-cancel"),
  reactivateSubscription: () => api.post("/api/billing/reactivate-subscription"),
  getNextBillDate: () => api.get("/api/billing/next-bill-date"),
  getSeats: () => api.get("/api/billing/seats"),
  getProrationPreview: (addSeats: number) => api.get("/api/billing/proration-preview", { params: { addSeats } }),
  getTaxInfo: () => api.get("/api/billing/tax-info"),
};

// ─── Public Payment ─────────────────────────────────────────────────────────
export const publicPayApi = {
  getInvoice: (token: string) => api.get(`/pay/${token}`),
  createCheckout: (token: string) => api.post(`/pay/${token}/checkout`),
  getStatus: (token: string) => api.get(`/pay/${token}/status`),
};

// ─── Firm ──────────────────────────────────────────────────────────────────
export const firmApi = {
  getProfile: () => api.get("/api/firm/profile"),
  updateProfile: (data: Record<string, any>) => api.put("/api/firm/profile", data),
  getOnboardingStatus: () => api.get("/api/firm/onboarding/status"),
  completeOnboardingStep: (step: string) => api.put("/api/firm/onboarding/step", { step }),
  completeOnboarding: () => api.put("/api/firm/onboarding/complete"),
  getGuidedTourStatus: () => api.get("/api/firm/guided-tour/status"),
  completeGuidedTour: () => api.put("/api/firm/guided-tour/complete"),
  uploadLogo: (formData: FormData) =>
    api.post("/api/firm/logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  removeLogo: () => api.delete("/api/firm/logo"),
  seedSampleData: () => api.post("/api/firm/seed-sample-data"),
  deleteSampleData: () => api.delete("/api/firm/sample-data"),
  getSampleDataStatus: () => api.get("/api/firm/sample-data/status"),
  transferOwnership: (newOwnerId: string) => api.post("/api/firm/transfer-ownership", { newOwnerId }),
  getCommunicationPreferences: () => api.get("/api/firm/communication-preferences"),
  updateCommunicationPreferences: (prefs: any) => api.put("/api/firm/communication-preferences", prefs),
  requestFeature: (data: { featureName: string; requiredPlan: string }) =>
    api.post("/api/firm/feature-request", data),
  getTerminology: () => api.get("/api/firm/terminology"),
};

// ─── Disbursement Presets ────────────────────────────────────────────────────
export const disbursementPresetsApi = {
  list: (params?: Record<string, any>) => api.get("/api/disbursement-presets", { params }),
  create: (data: Record<string, any>) => api.post("/api/disbursement-presets", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/disbursement-presets/${id}`, data),
  delete: (id: string) => api.delete(`/api/disbursement-presets/${id}`),
  remove: (id: string) => api.delete(`/api/disbursement-presets/${id}`),
  seed: () => api.post("/api/disbursement-presets/seed"),
};

// ─── Practice Area Case Data APIs ───────────────────────────────────────────
// Each practice area has: list, get, create, update, delete case data linked to matters
// Plus subdocument CRUD and pipeline stage changes

const createPracticeAreaApi = (prefix: string) => ({
  list: (params?: Record<string, any>) => api.get(`/api/${prefix}/cases`, { params }),
  get: (matterId: string) => api.get(`/api/${prefix}/cases/${matterId}`),
  create: (data: Record<string, any>) => api.post(`/api/${prefix}/cases`, data),
  update: (matterId: string, data: Record<string, any>) => api.put(`/api/${prefix}/cases/${matterId}`, data),
  delete: (matterId: string) => api.delete(`/api/${prefix}/cases/${matterId}`),
  changePipelineStage: (matterId: string, stageId: string) => api.put(`/api/${prefix}/cases/${matterId}/pipeline-stage`, { stageId }),
  // Subdocument CRUD (generic  -  subdoc name passed as param)
  addSubdoc: (matterId: string, subdoc: string, data: Record<string, any>) => api.post(`/api/${prefix}/cases/${matterId}/${subdoc}`, data),
  updateSubdoc: (matterId: string, subdoc: string, index: number, data: Record<string, any>) => api.put(`/api/${prefix}/cases/${matterId}/${subdoc}/${index}`, data),
  removeSubdoc: (matterId: string, subdoc: string, index: number) => api.delete(`/api/${prefix}/cases/${matterId}/${subdoc}/${index}`),
  // Program data
  getProgramData: (matterId: string) => api.get(`/api/${prefix}/cases/${matterId}/program-data`),
  updateProgramData: (matterId: string, data: Record<string, any>) => api.put(`/api/${prefix}/cases/${matterId}/program-data`, data),
  // Programs
  listPrograms: (params?: Record<string, any>) => api.get(`/api/${prefix}/programs`, { params }),
  seedPrograms: () => api.post(`/api/${prefix}/programs/seed`),
});

export const criminalApi = createPracticeAreaApi("criminal");
export const personalInjuryApi = createPracticeAreaApi("personal-injury");
export const familyApi = createPracticeAreaApi("family");
export const litigationApi = createPracticeAreaApi("litigation");
export const estatesApi = createPracticeAreaApi("estates");
export const classActionApi = createPracticeAreaApi("class-action");
export const corporateApi = createPracticeAreaApi("corporate");
export const employmentApi = createPracticeAreaApi("employment");
export const ipApi = createPracticeAreaApi("ip");
export const taxApi = createPracticeAreaApi("tax");
export const environmentalApi = createPracticeAreaApi("environmental");
export const bankruptcyApi = createPracticeAreaApi("bankruptcy");
export const adminLawApi = createPracticeAreaApi("admin-law");
export const constructionApi = createPracticeAreaApi("construction");
export const insuranceApi = createPracticeAreaApi("insurance");
export const entertainmentApi = createPracticeAreaApi("entertainment");
export const technologyApi = createPracticeAreaApi("technology");

// ─── Bills (Accounts Payable) ───────────────────────────────────────────────

export const billsApi = {
  list: (params?: Record<string, any>) => api.get("/api/bills", { params }),
  aging: () => api.get("/api/bills/aging"),
  get: (id: string) => api.get(`/api/bills/${id}`),
  create: (data: Record<string, any>) => api.post("/api/bills", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/bills/${id}`, data),
  remove: (id: string) => api.delete(`/api/bills/${id}`),
  approve: (id: string) => api.post(`/api/bills/${id}/approve`),
  pay: (id: string, data: Record<string, any>) => api.post(`/api/bills/${id}/pay`, data),
  void: (id: string) => api.post(`/api/bills/${id}/void`),
};

// ─── Expenses ───────────────────────────────────────────────────────────────

export const expensesApi = {
  list: (params?: Record<string, any>) => api.get("/api/expenses", { params }),
  summary: (params?: Record<string, any>) => api.get("/api/expenses/summary", { params }),
  get: (id: string) => api.get(`/api/expenses/${id}`),
  create: (data: Record<string, any>) => api.post("/api/expenses", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/expenses/${id}`, data),
  remove: (id: string) => api.delete(`/api/expenses/${id}`),
  scanReceipt: (formData: FormData) => api.post("/api/expenses/scan-receipt", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
};

// ─── Tax Settings ───────────────────────────────────────────────────────────

export const taxSettingsApi = {
  get: () => api.get("/api/tax-settings"),
  update: (data: Record<string, any>) => api.put("/api/tax-settings", data),
  summary: (params?: Record<string, any>) => api.get("/api/tax-settings/summary", { params }),
  autoConfigure: (data: Record<string, any>) => api.post("/api/tax-settings/auto-configure", data),
};

// ─── Inspector (Client Inspector Drawer) ────────────────────────────────────
export const inspectorApi = {
  get: (contactId: string) => api.get(`/api/inspector/${contactId}`),
  update: (contactId: string, data: Record<string, any>) => api.patch(`/api/inspector/${contactId}`, data),
};

// ─── Billing Rules ───────────────────────────────────────────────────────────

export const billingRulesApi = {
  list: (params?: Record<string, any>) => api.get("/api/billing-rules", { params }),
  create: (data: Record<string, any>) => api.post("/api/billing-rules", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/billing-rules/${id}`, data),
  remove: (id: string) => api.delete(`/api/billing-rules/${id}`),
  validateEntry: (data: Record<string, any>) => api.post("/api/billing-rules/validate-entry", data),
};

// ─── Activity Logs ───────────────────────────────────────────────────────────

export const activityLogsApi = {
  list: (params?: Record<string, any>) => api.get("/api/activity-logs", { params }),
  summary: () => api.get("/api/activity-logs/summary"),
  mine: (days?: number) => api.get("/api/activity-logs/mine", { params: { days } }),
};

// ─── Reviews ────────────────────────────────────────────────────────────────

export const reviewsApi = {
  list: (params?: Record<string, any>) => api.get("/api/reviews", { params }),
  stats: () => api.get("/api/reviews/stats"),
  get: (id: string) => api.get(`/api/reviews/${id}`),
  create: (data: Record<string, any>) => api.post("/api/reviews", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/reviews/${id}`, data),
  markRead: (id: string) => api.put(`/api/reviews/${id}/read`),
  approve: (id: string) => api.put(`/api/reviews/${id}/approve`),
  reject: (id: string) => api.put(`/api/reviews/${id}/reject`),
  remove: (id: string) => api.delete(`/api/reviews/${id}`),
  send: (data: Record<string, any>) => api.post("/api/reviews/send", data),
  bulkSend: (data: Record<string, any>) => api.post("/api/reviews/bulk-send", data),
  getLink: () => api.get("/api/reviews/link"),
  getSettings: () => api.get("/api/reviews/settings"),
  updateSettings: (data: Record<string, any>) => api.put("/api/reviews/settings", data),
};

// ─── Public Review (no auth) ──────────────────────────────────────────────
export const publicReviewApi = {
  get: (token: string) => publicApi.get(`/review/${token}`),
  submit: (token: string, data: Record<string, any>) => publicApi.post(`/review/${token}/submit`, data),
};

// ─── Google Business ─────────────────────────────────────────────────────────
export const googleBusinessApi = {
  getAuthUrl: () => api.get("/api/google-business/auth"),
  callback: (code: string, state: string) =>
    api.get("/api/google-business/callback", { params: { code, state } }),
  status: () => api.get("/api/google-business/status"),
  sync: () => api.post("/api/google-business/sync"),
  disconnect: () => api.delete("/api/google-business/disconnect"),
};

// ─── Message Templates ─────────────────────────────────────────────────────
export const messageTemplatesApi = {
  list: () => api.get("/api/message-templates"),
  get: (key: string) => api.get(`/api/message-templates/${key}`),
  update: (key: string, data: any) => api.put(`/api/message-templates/${key}`, data),
  reset: (key: string) => api.post(`/api/message-templates/${key}/reset`),
  preview: (key: string, variables: any) => api.post(`/api/message-templates/${key}/preview`, variables),
};

// ─── Reminder Schedule ─────────────────────────────────────────────────────
export const reminderScheduleApi = {
  get: () => api.get("/api/reminder-schedule"),
  update: (data: any) => api.put("/api/reminder-schedule", data),
};

// ─── Audiences ─────────────────────────────────────────────────────────────
export const audiencesApi = {
  list: (params?: Record<string, any>) => api.get("/api/audiences", { params }),
  get: (id: string) => api.get(`/api/audiences/${id}`),
  create: (data: Record<string, any>) => api.post("/api/audiences", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/audiences/${id}`, data),
  delete: (id: string) => api.delete(`/api/audiences/${id}`),
  recipients: (id: string, params?: Record<string, any>) => api.get(`/api/audiences/${id}/recipients`, { params }),
};

// ─── Campaigns ─────────────────────────────────────────────────────────────
export const campaignsApi = {
  list: (params?: Record<string, any>) => api.get("/api/campaigns", { params }),
  get: (id: string) => api.get(`/api/campaigns/${id}`),
  create: (data: Record<string, any>) => api.post("/api/campaigns", data),
  update: (id: string, data: Record<string, any>) => api.put(`/api/campaigns/${id}`, data),
  delete: (id: string) => api.delete(`/api/campaigns/${id}`),
  send: (id: string) => api.post(`/api/campaigns/${id}/send`),
  pause: (id: string) => api.post(`/api/campaigns/${id}/pause`),
  duplicate: (id: string) => api.post(`/api/campaigns/${id}/duplicate`),
  recipients: (id: string, params?: Record<string, any>) => api.get(`/api/campaigns/${id}/recipients`, { params }),
};

// ─── Conflict Checks ────────────────────────────────────────────────────
export const conflictChecksApi = {
  search: (data: { searchTerms: Array<{ name?: string; email?: string; phone?: string; type?: string }>; excludeClientId?: string; excludeMatterId?: string }) => api.post("/api/conflict-checks/search", data),
  resolve: (id: string, data: { status: string; notes?: string; waiverReason?: string }) => api.post(`/api/conflict-checks/resolve/${id}`, data),
  list: (params?: any) => api.get("/api/conflict-checks", { params }),
  get: (id: string) => api.get(`/api/conflict-checks/${id}`),
  forClient: (clientId: string) => api.get(`/api/conflict-checks/client/${clientId}`),
  forMatter: (matterId: string) => api.get(`/api/conflict-checks/matter/${matterId}`),
  autoCheckClient: (clientId: string) => api.post("/api/conflict-checks/auto-check-client", { clientId }),
  autoCheckMatter: (matterId: string) => api.post("/api/conflict-checks/auto-check-matter", { matterId }),
};

// ─── AI Matter Matching ───────────────────────────────────────────────────
export const aiMatterApi = {
  match: (data: { content: string; contentType: string; clientId?: string }) =>
    api.post("/api/matters/ai/match", data),
};

// ─── Screen Capture ─────────────────────────────────────────────────────────
export const screenCaptureApi = {
  start: () => api.post("/api/screen-capture/start"),
  frame: (data: { sessionId: string; imageBase64: string; previousHash?: string }) =>
    api.post("/api/screen-capture/frame", data),
  stop: (sessionId: string) => api.post("/api/screen-capture/stop", { sessionId }),
  generate: (sessionId: string) => api.post("/api/screen-capture/generate", { sessionId }),
  accept: (data: { sessionId: string; blocks: any[] }) => api.post("/api/screen-capture/accept", data),
  sessions: (params?: any) => api.get("/api/screen-capture/sessions", { params }),
  session: (sessionId: string) => api.get(`/api/screen-capture/sessions/${sessionId}`),
};

// ─── IP Docket ──────────────────────────────────────────────────────────────
export const ipDocketApi = {
  list: (params?: any) => api.get("/api/ip/docket", { params }),
  summary: () => api.get("/api/ip/docket/summary"),
  get: (id: string) => api.get(`/api/ip/docket/${id}`),
  compute: (data: { matterId: string; caseDataId: string }) => api.post("/api/ip/docket/compute", data),
  createManual: (data: any) => api.post("/api/ip/docket/manual", data),
  update: (id: string, data: any) => api.put(`/api/ip/docket/${id}`, data),
  complete: (id: string, data?: any) => api.post(`/api/ip/docket/${id}/complete`, data),
  waive: (id: string, data: any) => api.post(`/api/ip/docket/${id}/waive`, data),
  extend: (id: string, data: any) => api.post(`/api/ip/docket/${id}/extend`, data),
  delete: (id: string) => api.delete(`/api/ip/docket/${id}`),
  rules: () => api.get("/api/ip/docket/rules"),
  preferences: () => api.get("/api/ip/docket/preferences"),
  updatePreferences: (data: { disabledRuleIds?: string[]; customReminders?: { ruleId: string; extraReminderDays: number[] }[] }) =>
    api.put("/api/ip/docket/preferences", data),
};

// ─── IP Dashboard & Portfolio ───────────────────────────────────────────────
export const ipDashboardApi = {
  get: () => api.get("/api/ip/dashboard"),
  portfolio: (params?: any) => api.get("/api/ip/portfolio", { params }),
  clientPortfolio: (clientId: string) => api.get(`/api/ip/portfolio/client/${clientId}`),
};

// ─── Automations ────────────────────────────────────────────────────────────
export const automationsApi = {
  getConfig: () => api.get("/api/automations/config"),
  updateConfig: (data: any) => api.put("/api/automations/config", data),
  getLogs: (params?: any) => api.get("/api/automations/logs", { params }),
  getLog: (id: string) => api.get(`/api/automations/logs/${id}`),
  getStats: () => api.get("/api/automations/stats"),
  test: (message: string) => api.post("/api/automations/test", { message }),
};

export default api;
